import { flow, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import { ReleaseInfoModel } from '../models/Navbar/ReleaseInfoModel'
import { OperationFailure, OperationInProgress, OperationNotStarted, OperationStatusModel, OperationSuccess } from '../models/common/OperationStatus'
import { WebSocketConnection } from '../utils/WebSocketConnection'
import { CenterOpenState } from '../models/Navbar/CenterOpenState'

interface CenterStateTransition {
  open_state: CenterOpenState
}

type BackendConfiguration = {
  release_version?: string,
  backend_version: string,
}

export const NavbarStore = FetchModel
  .named('NavBarStore')
  .props({
    releaseInfo: types.maybeNull(ReleaseInfoModel),

    centerStatus: types.maybeNull(types.enumeration('CenterOpenState', Object.values(CenterOpenState))),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted())
  })
  .views((self) => ({
    get gcsVersionUrl(): string {
      return `${self.baseUrl}version/`
    },
    get oversightVersionUrl(): string {
      return `${self.oversightBaseUrl}configuration/`
    },
  }))
  .actions((self) => ({
    _updateCenterStatus(centerStatusUpdate: CenterStateTransition) {
      self.centerStatus = centerStatusUpdate.open_state
    },
    onWebsocketDisconnected() {
      self.centerStatus = null
    },
  }))
  .actions((self) => ({
    _fetchAndSetReleaseInfo: flow(function* fetchAndSetReleaseInfo() {
      self.fetchStatus = OperationInProgress()

      const gcsVersion = yield* toGenerator(
        self.loadObjectOrError<BackendConfiguration>(self.gcsVersionUrl, {}, { fetchOperationName: 'Fetch backend release info' }),
      )

      const oversightConfiguration = yield* toGenerator(
        self.loadObjectOrError<BackendConfiguration>(self.oversightVersionUrl, {}, { fetchOperationName: 'Fetch backend release info' }),
      )

      const error = gcsVersion.error || oversightConfiguration.error
      if (error) {
        self.fetchStatus = OperationFailure(error)

        return
      } 

      self.releaseInfo = ReleaseInfoModel.create({
        gcs_backend_version: gcsVersion.result.backend_version,
        oversight_backend_version: oversightConfiguration.result.backend_version,
      })

      self.fetchStatus = OperationSuccess()
    }),
    _subscribeToCenterStatusUpdates() {
      // TODO: take care of error if this returns error
      void WebSocketConnection.subscribeToSubject('center_open_state', self._updateCenterStatus)
      WebSocketConnection.subscribeToWebsocketDisconnect(self.onWebsocketDisconnected)
    }
  }))
  .actions((self) => ({
    initializeNavbar() {
      void self._fetchAndSetReleaseInfo()
      self._subscribeToCenterStatusUpdates()
    }
  }))
